/**
 * Cloud transport: vms_database, metadata_service, the agent and, through the relay, the media
 * servers. Every URL is same-origin: the edge routes /vms, /relay, /agent and /metadata.
 *
 * An org-scoped route takes ?orgId=, read from the auth session at each call, so an org switch
 * needs no new transport. A call without an active organization throws.
 */

import { z } from 'zod';

import {
    buildAuditQuery, buildBookmarksQuery, buildClipDownloadPath, buildFramesQuery,
    buildHealthLogQuery, buildImageQuery, createBoundary, fetchBlob, fetchText,
    getHeatmapRecordsQuery, getSegmentsQuery, postAction, queryOf,
} from '../client';
import { REQUEST_TIMEOUT_MS } from '../http';
import { keyBy, listOf } from '../listOf';
import { logFilePath, logFileRange } from '../logTransfer';
import { MeResponseSchema } from '../transformers/auth';
import {
    CaseAttachmentContentSchema, CaseAttachmentSchema, CaseDetailSchema, CaseSchema,
} from '../transformers/cases';
import {
    DeviceFramesSchema, DiscoverResultsSchema, FlatDeviceSchema, getDeviceKey,
} from '../transformers/devices';
import { getCounterPathKey, getRuleKey } from '../transformers/healthCounters';
import { authSession } from '@/auth/authSession';
import { uuid } from '@/lib/uuid';
import type { AgentLoadProgress } from '@/types/agentLoad';
import { ToolboxStatusSchema } from '@/types/agentLoad';
import type { Agent, AgentSettings, Rule, RulesSchema } from '@/types/agents';
import {
    AgentSchema, AgentSettingsSchema, getAgentKey, RuleCreatedSchema, RuleSchema,
    RulesSchemaSchema, TriggerCountSchema,
} from '@/types/agents';
import type { SearchParseResult } from '@/types/aiSearch';
import { SearchParseResultSchema } from '@/types/aiSearch';
import type { VmsApi } from '@/types/api';
import type { AuditRecord } from '@/types/audit';
import { AuditRecordSchema } from '@/types/audit';
import type {
    MembershipPermissions, MeResponse, RevokeSessionsResponse, User, UserDeleteResponse,
} from '@/types/auth';
import {
    MembershipPermissionsSchema, RevokeSessionsResponseSchema, UserDeleteResponseSchema,
    UserSchema,
} from '@/types/auth';
import type { BinaryFile } from '@/types/binaryFiles';
import { BinaryFileSchema } from '@/types/binaryFiles';
import type { Bookmark, BookmarkDraft, BookmarkTag } from '@/types/bookmarks';
import { BookmarksAcceptedSchema, BookmarkSchema, BookmarkTagSchema } from '@/types/bookmarks';
import type { Case, CaseAttachment, CaseAttachmentContent, CaseDetail } from '@/types/cases';
import type { Boundary } from '@/types/contract';
import { ViolationSourceKind } from '@/types/contract';
import type {
    CompactSegments, DeviceFrame, DeviceSuggestions, DeviceWebSession, DiscoverResult, FlatDevice,
    ImageOpts, ImageQuality,
} from '@/types/devices';
import {
    CompactSegmentsSchema, DeviceSuggestionsSchema, DeviceWebSessionSchema,
} from '@/types/devices';
import type {
    Enrollment, EnrollmentDetail, EnrollmentSettings, EnrollResponse, ReidentifyResponse,
} from '@/types/enrollments';
import {
    EnrollmentDetailSchema, EnrollmentSchema, EnrollmentSettingsSchema, EnrollResponseSchema,
    ReidentifyResponseSchema,
} from '@/types/enrollments';
import type { EmbeddingHit, EventSearchResult } from '@/types/events';
import {
    EmbeddingHitSchema, EventMetadataWithSimilaritySchema, EventSearchResultSchema,
} from '@/types/events';
import type {
    HealthCounterDescription, HealthCounterValues, HealthRule,
} from '@/types/healthCounters';
import {
    HealthCounterDescriptionSchema, HealthCounterValuesSchema, HealthRuleSchema,
} from '@/types/healthCounters';
import { type HealthMessagePayload, HealthMessagePayloadSchema } from '@/types/healthMessages';
import type { HeatmapRecord, HeatmapSegments } from '@/types/heatmap';
import { HeatmapRecordSchema, HeatmapSegmentsSchema } from '@/types/heatmap';
import type { Layout } from '@/types/layouts';
import { LayoutSchema } from '@/types/layouts';
import type { LogFileContent } from '@/types/logs';
import type { LogFile, LogSettings } from '@/types/logs';
import { getLogFileKey, LogFileSchema, LogSettingsSchema } from '@/types/logs';
import type { ObjectCheckResult } from '@/types/objectCheck';
import { ObjectCheckResultSchema } from '@/types/objectCheck';
import type {
    ArchiveEncryptionStatus, AvailableUpdates, Build, ClaimedServer, Location, LocationWithServers,
    ManifestStatus, PendingServer, Server, ServerInfo, ServerLocalAddresses, ServerReplacement,
    ServerSettings, ServerTime, ServerUpdateStatus, Storage, StorageWithStatus, UpdatePlan,
} from '@/types/servers';
import {
    ArchiveEncryptionStatusSchema, AvailableUpdatesSchema, BuildSchema, ClaimedServerSchema,
    LocationSchema, LocationWithServersSchema, ManifestStatusSchema, PendingServerSchema,
    ServerInfoSchema, ServerLocalAddressesSchema, ServerReplacementSchema, ServerSchema,
    ServerSettingsPatchSchema, ServerSettingsSchema, ServerTimeSchema, ServerUpdateStatusSchema,
    StorageSchema, StoragesSchema, StorageWithStatusSchema, UpdatePlanSchema,
} from '@/types/servers';
import type { TelegramLink, TelegramStatus } from '@/types/telegram';
import { TelegramLinkSchema, TelegramStatusSchema } from '@/types/telegram';
import type { TurnCredentials } from '@/types/turn';
import { TurnCredentialsSchema } from '@/types/turn';

// Appends a query fragment to a URL, or joins two bare `key=value` fragments with `&`.
function addQuery(urlOrQuery: string, query?: string): string {
    if (!query) {
        return urlOrQuery;
    }
    const isUrl = /^[a-z]+:\/\//i.test(urlOrQuery) || urlOrQuery.startsWith('/');
    const sep = isUrl ? (urlOrQuery.includes('?') ? '&' : '?') : '&';
    return `${urlOrQuery}${sep}${query}`;
}

function requireOrg(): string {
    const orgId = authSession.getActiveOrg();
    if (!orgId) {
        throw new Error('cloud transport: no active organization (call setActiveOrg first)');
    }
    return orgId;
}

export function createCloudTransport(): VmsApi {
    // /vms/api/v1 paths, with ?orgId= injected for the org-scoped routes.
    const v1 = (path: string, extraQuery = ''): string =>
        addQuery(
            `/vms/api/v1${path}`,
            addQuery(`orgId=${requireOrg()}`, extraQuery),
        );

    // About the caller, not one organization's data: no ?orgId=.
    const getVmsIdentityUrl = (path: string): string => `/vms/api/v1${path}`;

    // metadata_service guards its org-scoped routes by ?orgId=, as vms_database does.
    const meta = (path: string, extraQuery = ''): string =>
        addQuery(`/metadata/api/v1${path}`,
            addQuery(`orgId=${requireOrg()}`, extraQuery));
    const {
        deleteParsed, fetchParsed, fetchRows, patchParsed, postFormParsed, postParsed, putParsed,
    } = createBoundary({ kind: ViolationSourceKind.Cloud });

    // A request through the relay is answered by the media_server it names.
    const getRelayBoundary = (serverId: string): Boundary =>
        createBoundary({
            kind: ViolationSourceKind.Relay,
            serverId,
        });

    const getDeviceImageUrl = (id: string, quality?: ImageQuality, opts?: ImageOpts): string => {
        if (!opts?.serverId) {
            const fix = 'pass camera.serverId from the caller';
            throw new Error(`cloud deviceImageUrl requires opts.serverId — ${fix}`);
        }
        const query = buildImageQuery(quality, opts);
        const base = `/relay/proxy/${opts.serverId}/api/v1/devices/${id}`;
        return `${base}/image${query}`;
    };

    return {
        // Identity
        getMe: (): Promise<MeResponse> => fetchParsed(getVmsIdentityUrl('/me'), MeResponseSchema),
        acceptInvite: (orgId): Promise<MeResponse> =>
            postParsed(getVmsIdentityUrl('/invite/accept'), { orgId }, MeResponseSchema),
        createOrg: (orgName): Promise<MeResponse> =>
            postParsed(getVmsIdentityUrl('/setup'), { orgName }, MeResponseSchema),

        // Devices
        listDevices: (): Promise<FlatDevice[]> =>
            fetchRows(v1('/devices'), FlatDeviceSchema, { getKey: getDeviceKey }),
        getDevice: (id): Promise<FlatDevice> =>
            fetchParsed(v1(`/devices/${id}`), FlatDeviceSchema),
        // The owning media_server adds the camera, or refuses it with a reason. The cloud row
        // follows through its upload, so a refused add leaves no row.
        addDevice: (data): Promise<FlatDevice> => {
            if (!data.serverId) {
                throw new Error(
                    'cloud addDevice requires data.serverId — the server that owns the camera');
            }
            return getRelayBoundary(data.serverId).postParsed(
                `/relay/proxy/${data.serverId}/api/v1/devices`, data,
                FlatDeviceSchema,
            );
        },
        updateDevice: (id, patch): Promise<FlatDevice> =>
            patchParsed(v1(`/devices/${id}`), patch, FlatDeviceSchema),

        // What only the media_server knows: the status, the encoder capabilities, the talk
        // targets.
        getDeviceLive: (id, opts): Promise<FlatDevice> => {
            if (!opts?.serverId) {
                throw new Error(
                    'cloud getDeviceLive requires opts.serverId — pass camera.serverId from the ' +
                    'caller');
            }
            return getRelayBoundary(opts.serverId).fetchParsed(
                `/relay/proxy/${opts.serverId}/api/v1/devices/${id}`,
                FlatDeviceSchema,
            );
        },
        // The media_server validates the profile picks; the cloud row follows by changelog.
        updateDeviceLive: (id, patch, opts): Promise<FlatDevice> => {
            if (!opts?.serverId) {
                throw new Error(
                    'cloud updateDeviceLive requires opts.serverId — pass camera.serverId from ' +
                    'the caller');
            }
            return getRelayBoundary(opts.serverId).patchParsed(
                `/relay/proxy/${opts.serverId}/api/v1/devices/${id}`, patch,
                FlatDeviceSchema,
            );
        },
        removeDevice: (id): Promise<null> => deleteParsed(v1(`/devices/${id}`), z.null()),
        // The camera is rebound on its media_server; the cloud rows follow by changelog.
        replaceDevice: (id, sourceId, opts): Promise<FlatDevice> => {
            if (!opts?.serverId) {
                throw new Error(
                    'cloud replaceDevice requires opts.serverId — pass camera.serverId from the ' +
                    'caller');
            }
            const path = `/api/v1/devices/${id}/replace`;
            return getRelayBoundary(opts.serverId).postParsed(
                `/relay/proxy/${opts.serverId}${path}`, { sourceId },
                FlatDeviceSchema,
            );
        },
        undoDeviceReplacement: (id, opts): Promise<FlatDevice> => {
            if (!opts?.serverId) {
                throw new Error(
                    'cloud undoDeviceReplacement requires opts.serverId — pass camera.serverId ' +
                    'from the caller');
            }
            const path = `/api/v1/devices/${id}/undo-replacement`;
            return getRelayBoundary(opts.serverId).postParsed(
                `/relay/proxy/${opts.serverId}${path}`, {}, FlatDeviceSchema,
            );
        },
        // Only the camera's media_server holds its connection, so this goes through the relay.
        probeDevice: (id, opts): Promise<void> => {
            if (!opts?.serverId) {
                throw new Error('cloud probeDevice requires opts.serverId: pass camera.serverId');
            }
            return postAction(
                `/relay/proxy/${opts.serverId}/api/v1/devices/${id}/probe`);
        },
        // The cloud mints the proxied origin. Device ids repeat across servers, so serverId too.
        createDeviceWebSession: (id, opts): Promise<DeviceWebSession> =>
            postParsed(v1(`/devices/${id}/web-sessions`,
                `serverId=${encodeURIComponent(opts.serverId)}`), {}, DeviceWebSessionSchema),
        // The "Find" step of discover-then-add: the media_server probes, and adds nothing.
        discoverCameras: (serverId, entries): Promise<DiscoverResult[]> => {
            if (!serverId) {
                throw new Error('cloud discoverCameras requires a serverId');
            }
            return getRelayBoundary(serverId).postParsed(
                `/relay/proxy/${serverId}/api/v1/devices/discover`, { entries },
                DiscoverResultsSchema,
            );
        },
        getDeviceImageUrl,
        getDevicePreview: (id, { serverId, signal }): Promise<Blob | null> =>
            fetchBlob(getDeviceImageUrl(id, 'low', { serverId }), { signal }),
        // Each sample carries the moment it was asked for.
        getDeviceFrames: (id, opts): Promise<DeviceFrame[]> => {
            if (!opts.serverId) {
                throw new Error(
                    'cloud getDeviceFrames requires opts.serverId — pass camera.serverId from ' +
                    'the caller');
            }
            const base = `/relay/proxy/${opts.serverId}/api/v1/devices/${id}`;
            return getRelayBoundary(opts.serverId).fetchParsed(
                `${base}/frames${buildFramesQuery(opts)}`, DeviceFramesSchema,
            );
        },

        getSegments: (cameraId, opts = {}): Promise<CompactSegments> => {
            if (!opts.serverId) {
                throw new Error(
                    'cloud getSegments requires opts.serverId — pass camera.serverId from the ' +
                    'caller');
            }
            const params = new URLSearchParams();
            if (opts.startTimeMs !== undefined) {
                params.set('startTimeMs', String(opts.startTimeMs));
            }
            if (opts.endTimeMs !== undefined) {
                params.set('endTimeMs', String(opts.endTimeMs));
            }
            const query = params.toString();
            const url = `/relay/proxy/${opts.serverId}/api/v1/segments/${cameraId}` +
                (query === '' ? '' : `?${query}`);
            // A signal replaces the default deadline of a request, so the deadline joins it here.
            const init = opts.signal === undefined ? {} : {
                signal: AbortSignal.any([opts.signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)]),
            };
            return getRelayBoundary(opts.serverId).fetchParsed(url, CompactSegmentsSchema, init);
        },

        // The cookie authenticates the upgrade: a WebSocket carries no Authorization header.
        getWebrtcUrl: (cameraId, quality = 'high', opts = {}): string => {
            if (!opts.serverId) {
                throw new Error(
                    'cloud getWebrtcUrl requires opts.serverId — pass camera.serverId from the ' +
                    'caller');
            }
            const proto = location.protocol === 'https:' ? 'wss' : 'ws';
            // The dev server's WebSocket proxy can wedge, and then every later upgrade waits
            // forever; VITE_WS_HOST sends the upgrade past it, straight to the cloud edge.
            const wsHost = import.meta.env.DEV ? import.meta.env.VITE_WS_HOST : undefined;
            const host = wsHost ? `wss://${wsHost}` : `${proto}://${location.host}`;
            // A session is codec-locked at SDP time, so it opens at the archive position. Older
            // servers open live, and the caller's parked seek applies.
            const position = opts.positionMs !== undefined && opts.positionMs >= 0
                ? `&positionMs=${Math.round(opts.positionMs)}` : '';
            // The first metadata subscription; older servers ignore it.
            const withMetadata = opts.withMetadata !== undefined
                ? `&withMetadata=${opts.withMetadata}` : '';
            // The edge pins /relay/proxy-ws to HTTP/1.1: h2c cannot carry a WebSocket upgrade.
            const base = `${host}/relay/proxy-ws/${opts.serverId}` +
                `/api/v1/streaming/webrtc/${cameraId}`;
            return `${base}?quality=${quality}${position}${withMetadata}`;
        },
        // Server-Sent Events, parsed in api/notifications.ts.
        getNotificationsUrl: (): string => {
            const host = location.origin;
            return `${host}/vms/api/v1/notifications?orgId=${requireOrg()}`;
        },
        // Cookie auth; lib/webrtc adds these to its STUN list.
        getTurnCredentials: (): Promise<TurnCredentials> =>
            fetchParsed('/relay/turn/credentials', TurnCredentialsSchema),
        // NDJSON is plain HTTP, so it rides /relay/proxy; streamAiSearch reads the stream.
        getAiSearchUrl: (deviceId, opts): string => {
            if (!opts.serverId) {
                throw new Error(
                    'cloud aiSearchUrl requires opts.serverId — pass camera.serverId from the ' +
                    'caller');
            }
            const query = new URLSearchParams({
                byEmbedding: 'true',
                crop: opts.crop,
                startTimeMs: String(opts.startTimeMs),
                endTimeMs: String(opts.endTimeMs),
                deadlineMs: String(opts.deadlineMs),
            });
            if (opts.similarityThreshold !== undefined) {
                query.set('similarityThreshold', String(opts.similarityThreshold));
            }
            return `/relay/proxy/${opts.serverId}/api/v1/devices/${deviceId}/ai-search?${query}`;
        },
        // SSE is plain HTTP, so it rides /relay/proxy, not /relay/proxy-ws.
        getRulesMonitorUrl: (ruleId, deviceId, opts = {}): string => {
            if (!opts.serverId) {
                throw new Error(
                    'cloud rulesMonitorUrl requires opts.serverId — pass camera.serverId from ' +
                    'the caller');
            }
            return `/relay/proxy/${opts.serverId}/api/v1/rules/monitor` +
                `?ruleId=${encodeURIComponent(ruleId)}&deviceId=${deviceId}`;
        },
        // An <a href>: the cookie carries the session through the relay.
        clipDownloadUrl: (cameraId, opts): string => {
            if (!opts.serverId) {
                throw new Error('cloud clipDownloadUrl requires opts.serverId');
            }
            const base = `/relay/proxy/${opts.serverId}`;
            return `${base}${buildClipDownloadPath(cameraId, opts)}`;
        },
        // The cookie authenticates the handshake, so the URL carries no token.
        getAgentWsUrl: (conversationId): string => {
            const proto = location.protocol === 'https:' ? 'wss' : 'ws';
            const host = `${proto}://${location.host}`;
            return `${host}/agent/ws?orgId=${requireOrg()}` +
                `&conversationId=${encodeURIComponent(conversationId)}`;
        },
        parseSearchText: (request): Promise<SearchParseResult> => postParsed(
            addQuery('/agent/api/v1/search/parse', `orgId=${requireOrg()}`),
            request,
            SearchParseResultSchema,
        ),
        checkObjects: (request): Promise<ObjectCheckResult> => postParsed(
            addQuery('/agent/api/v1/search/check-objects', `orgId=${requireOrg()}`),
            request,
            ObjectCheckResultSchema,
        ),
        // Agents
        listAgents: (): Promise<Agent[]> =>
            fetchRows(v1('/agents'), AgentSchema, { getKey: getAgentKey }),
        getAgentSettings: (agentId): Promise<AgentSettings> =>
            fetchParsed(v1(`/agents/${agentId}/settings`), AgentSettingsSchema),
        patchAgentSettings: (agentId, values): Promise<AgentSettings> =>
            patchParsed(v1(`/agents/${agentId}/settings`), values, AgentSettingsSchema),
        getAgentDeviceSettings: (agentId, deviceId): Promise<AgentSettings> =>
            fetchParsed(v1(`/agents/${agentId}/devices/${deviceId}/settings`),
                AgentSettingsSchema),
        patchAgentDeviceSettings: (agentId, deviceId, values): Promise<AgentSettings> =>
            patchParsed(v1(`/agents/${agentId}/devices/${deviceId}/settings`), values,
                AgentSettingsSchema),

        // Rules. The cloud's POST is a batch upsert answering only a count, so the id is
        // minted here, as the devices' and layouts' are.
        listRules: (): Promise<Rule[]> =>
            fetchRows(v1('/rules'), RuleSchema, { getKey: keyBy('id') }),
        getRule: (id): Promise<Rule> => fetchParsed(v1(`/rules/${id}`), RuleSchema),
        createRule: async (data, given): Promise<string> => {
            const id = given ?? uuid();
            await postParsed(v1('/rules'), [{ ...data, id }], RuleCreatedSchema);
            return id;
        },
        patchRule: (id, patch): Promise<Rule> =>
            patchParsed(v1(`/rules/${id}`), patch, RuleSchema),
        getRuleTriggerCount: async (id): Promise<number> =>
            (await fetchParsed(v1(`/rules/${id}/triggers`), TriggerCountSchema)).count,
        resetRuleTriggerCount: (id): Promise<null> =>
            deleteParsed(v1(`/rules/${id}/triggers`), z.null()),
        deleteRule: (id): Promise<null> => deleteParsed(v1(`/rules/${id}`), z.null()),
        getRulesSchema: (): Promise<RulesSchema> =>
            fetchParsed(v1('/rules/schema'), RulesSchemaSchema),

        // Layouts: the id is minted here, so a create opens the view at once.
        listLayouts: (): Promise<Layout[]> => fetchRows(v1('/layouts'), LayoutSchema),
        createLayout: (data): Promise<Layout> =>
            postParsed(v1('/layouts'), { id: uuid(), ...data }, LayoutSchema),
        patchLayout: (id, patch): Promise<Layout> =>
            patchParsed(v1(`/layouts/${id}`), patch, LayoutSchema),
        deleteLayout: (id): Promise<null> => deleteParsed(v1(`/layouts/${id}`), z.null()),

        // Cases — investigations. A listing carries each case with what is filed
        // into it; a write answers the case alone.
        listCases: (): Promise<CaseDetail[]> => fetchRows(v1('/cases'), CaseDetailSchema),
        createCase: (title): Promise<Case> => postParsed(v1('/cases'), { title },
            CaseSchema),
        patchCase: (id, patch): Promise<Case> =>
            patchParsed(v1(`/cases/${id}`), patch, CaseSchema),
        deleteCase: (id): Promise<null> => deleteParsed(v1(`/cases/${id}`), z.null()),
        attachCaseBookmark: (caseId, bookmarkId): Promise<Bookmark> =>
            postParsed(v1(`/cases/${caseId}/bookmarks`), { bookmarkId }, BookmarkSchema),
        detachCaseBookmark: (caseId, bookmarkId): Promise<null> =>
            deleteParsed(v1(`/cases/${caseId}/bookmarks/${bookmarkId}`), z.null()),
        uploadCaseAttachment: (caseId, file, note = ''): Promise<CaseAttachment> => {
            const form = new FormData();
            form.set('name', file.name);
            form.set('note', note);
            form.set('file', file, file.name);
            return postFormParsed(
                v1(`/cases/${caseId}/attachments`), form, CaseAttachmentSchema);
        },
        patchCaseAttachment: (caseId, id, patch): Promise<CaseAttachment> =>
            patchParsed(v1(`/cases/${caseId}/attachments/${id}`), patch,
                CaseAttachmentSchema),
        deleteCaseAttachment: (caseId, id): Promise<null> =>
            deleteParsed(v1(`/cases/${caseId}/attachments/${id}`), z.null()),
        getCaseAttachmentContent: (caseId, id): Promise<CaseAttachmentContent> =>
            fetchParsed(
                v1(`/cases/${caseId}/attachments/${id}/content`),
                CaseAttachmentContentSchema),

        // Audio clips that a rule action plays; the URL rides the cookie.
        listBinaryFiles: (): Promise<BinaryFile[]> =>
            fetchRows(v1('/binary-files'), BinaryFileSchema),
        uploadBinaryFile: (name, file): Promise<BinaryFile> => {
            const form = new FormData();
            form.set('name', name);
            form.set('file', file, file.name);
            return postFormParsed(v1('/binary-files'), form, BinaryFileSchema);
        },
        deleteBinaryFile: (id): Promise<void> =>
            deleteParsed(v1(`/binary-files/${id}`), z.null()).then(() => undefined),
        getBinaryFileUrl: (id): string => v1(`/binary-files/${id}`),

        // Only the ephemeral view state of the wall; the edge pins the route to HTTP/1.1.
        getVideoWallWsUrl: (id): string => {
            const proto = location.protocol === 'https:' ? 'wss' : 'ws';
            const host = `${proto}://${location.host}`;
            return `${host}/vms/api/v1/videowalls/${id}/ws?orgId=${requireOrg()}`;
        },

        // Servers
        listServers: (): Promise<Server[]> => fetchRows(v1('/servers'), ServerSchema),
        renameServer: (id, name): Promise<Server> =>
            patchParsed(v1(`/servers/${id}`), { name }, ServerSchema),
        removeServer: (id): Promise<null> => deleteParsed(v1(`/servers/${id}`), z.null()),
        replaceServer: (id, targetServerId): Promise<ServerReplacement> =>
            postParsed(v1(`/servers/${id}/replace`), { targetServerId },
                ServerReplacementSchema),

        // The media_server holds its settings: online servers only. PATCH answers them all.
        getServerSettings: (serverId): Promise<ServerSettings> => {
            if (!serverId) {
                throw new Error('cloud getServerSettings requires a serverId');
            }
            return getRelayBoundary(serverId).fetchParsed(
                `/relay/proxy/${serverId}/api/v1/server/settings`,
                ServerSettingsSchema,
            );
        },
        // deviceId -> [suggestions] for every camera on the server; {} when healthy.
        getSuggestions: (serverId): Promise<DeviceSuggestions> => {
            if (!serverId) {
                throw new Error('cloud getSuggestions requires a serverId');
            }
            return getRelayBoundary(serverId).fetchParsed(
                `/relay/proxy/${serverId}/api/v1/suggestions`,
                DeviceSuggestionsSchema,
            );
        },
        updateServerSettings: (serverId, patch): Promise<ServerSettings> => {
            if (!serverId) {
                throw new Error('cloud updateServerSettings requires a serverId');
            }
            return getRelayBoundary(serverId).patchParsed(
                `/relay/proxy/${serverId}/api/v1/server/settings`,
                z.encode(ServerSettingsPatchSchema, patch),
                ServerSettingsSchema,
            );
        },

        getServerLocalAddresses: (serverId): Promise<ServerLocalAddresses> => {
            if (!serverId) {
                throw new Error('cloud getServerLocalAddresses requires a serverId');
            }
            return getRelayBoundary(serverId).fetchParsed(
                `/relay/proxy/${serverId}/api/v1/server/local-addresses`,
                ServerLocalAddressesSchema,
            );
        },

        // With their live state, so online servers only.
        listStoragesWithStatus: (serverId): Promise<StorageWithStatus[]> => {
            if (!serverId) {
                throw new Error('cloud listStoragesWithStatus requires a serverId');
            }
            return getRelayBoundary(serverId).fetchRows(
                `/relay/proxy/${serverId}/api/v1/server/storages`,
                StorageWithStatusSchema,
                { getKey: keyBy('id') },
            );
        },
        // The cloud's mirror: settings only, also for an offline server.
        listServerStorages: (serverId): Promise<Storage[]> => {
            if (!serverId) {
                throw new Error('cloud listServerStorages requires a serverId');
            }
            return fetchRows(
                v1(`/storages?serverId=${encodeURIComponent(serverId)}`),
                StorageSchema,
                { getKey: keyBy('id') },
            );
        },
        // The stored rows: an offline server shows its last known version.
        listUpdateStatus: (): Promise<ServerUpdateStatus[]> =>
            fetchRows(v1('/update-status'), ServerUpdateStatusSchema),
        // Build registry: about the deployment, so no ?orgId=.
        getAdminManifest: (): Promise<ManifestStatus> =>
            fetchParsed(getVmsIdentityUrl('/admin/manifest'), ManifestStatusSchema),
        getAdminBuilds: (): Promise<Build[]> => fetchRows(getVmsIdentityUrl('/admin/builds'),
            BuildSchema),
        publishBuild: (version): Promise<Build> =>
            postParsed(getVmsIdentityUrl('/admin/builds'), { version }, BuildSchema),
        withdrawBuild: (version): Promise<Build> => postParsed(
            getVmsIdentityUrl(`/admin/builds/${encodeURIComponent(version)}/withdraw`), {},
            BuildSchema,
        ),
        archiveBuild: (version): Promise<Build> => postParsed(
            getVmsIdentityUrl(`/admin/builds/${encodeURIComponent(version)}/archive`), {},
            BuildSchema,
        ),
        restoreBuild: (version): Promise<Build> => postParsed(
            getVmsIdentityUrl(`/admin/builds/${encodeURIComponent(version)}/restore`), {},
            BuildSchema,
        ),
        getAvailableUpdates: (): Promise<AvailableUpdates> =>
            fetchParsed(v1('/updates/available'), AvailableUpdatesSchema),
        listUpdatePlans: (): Promise<UpdatePlan[]> =>
            fetchRows(v1('/update-plans'), UpdatePlanSchema),
        createUpdatePlan: (req): Promise<UpdatePlan> =>
            postParsed(v1('/update-plans'), req, UpdatePlanSchema),
        createPrivateUpdatePlan: (req): Promise<UpdatePlan> =>
            postParsed(v1('/update-plans/private'), req, UpdatePlanSchema),
        installUpdatePlanNow: (id): Promise<UpdatePlan> => postParsed(
            v1(`/update-plans/${encodeURIComponent(id)}/install-now`), {}, UpdatePlanSchema,
        ),
        cancelUpdatePlan: (id): Promise<UpdatePlan> =>
            deleteParsed(v1(`/update-plans/${encodeURIComponent(id)}`), UpdatePlanSchema),
        addServerStorage: (serverId, url): Promise<StorageWithStatus[]> => {
            if (!serverId) {
                throw new Error('cloud addServerStorage requires a serverId');
            }
            return getRelayBoundary(serverId).postParsed(
                `/relay/proxy/${serverId}/api/v1/server/storages`,
                { url },
                StoragesSchema,
            );
        },
        updateServerStorage: (serverId, storageId, patch): Promise<StorageWithStatus[]> => {
            if (!serverId) {
                throw new Error('cloud updateServerStorage requires a serverId');
            }
            const base = `/relay/proxy/${serverId}/api/v1/server/storages`;
            return getRelayBoundary(serverId).patchParsed(
                `${base}/${encodeURIComponent(storageId)}`, patch, StoragesSchema,
            );
        },
        startStorageRebuild: (serverId, storageId): Promise<unknown> => {
            if (!serverId) {
                throw new Error('cloud startStorageRebuild requires a serverId');
            }
            const base = `/relay/proxy/${serverId}/api/v1/server/storages`;
            return getRelayBoundary(serverId).postParsed(
                `${base}/${encodeURIComponent(storageId)}/rebuild`, {}, z.unknown(),
            );
        },
        stopStorageRebuild: (serverId, storageId): Promise<unknown> => {
            if (!serverId) {
                throw new Error('cloud stopStorageRebuild requires a serverId');
            }
            const base = `/relay/proxy/${serverId}/api/v1/server/storages`;
            return getRelayBoundary(serverId).deleteParsed(
                `${base}/${encodeURIComponent(storageId)}/rebuild`, z.unknown(),
            );
        },
        removeServerStorage: (serverId, storageId): Promise<StorageWithStatus[]> => {
            if (!serverId) {
                throw new Error('cloud removeServerStorage requires a serverId');
            }
            const base = `/relay/proxy/${serverId}/api/v1/server/storages`;
            return getRelayBoundary(serverId).deleteParsed(
                `${base}/${encodeURIComponent(storageId)}`, StoragesSchema,
            );
        },

        getServerTime: (serverId): Promise<ServerTime> => {
            if (!serverId) {
                throw new Error('cloud getServerTime requires a serverId');
            }
            return getRelayBoundary(serverId).fetchParsed(
                `/relay/proxy/${serverId}/api/v1/time`, ServerTimeSchema,
            );
        },

        // Served by media servers and analytics servers.
        getServerInfo: (serverId): Promise<ServerInfo> => {
            if (!serverId) {
                throw new Error('cloud getServerInfo requires a serverId');
            }
            return getRelayBoundary(serverId).fetchParsed(
                `/relay/proxy/${serverId}/api/v1/server/info`, ServerInfoSchema,
            );
        },

        getAgentLoadProgress: async (serverId): Promise<AgentLoadProgress> => (
            await getRelayBoundary(serverId).fetchParsed(
                `/relay/proxy/${serverId}/api/v1/status`, ToolboxStatusSchema,
            )
        ).agents,

        // The logs are on the node only.
        getLogSettings: (serverId): Promise<LogSettings> => {
            if (!serverId) {
                throw new Error('cloud getLogSettings requires a serverId');
            }
            return getRelayBoundary(serverId).fetchParsed(
                `/relay/proxy/${serverId}/api/v1/logs/settings`, LogSettingsSchema,
            );
        },
        updateLogSettings: (serverId, doc): Promise<LogSettings> => {
            if (!serverId) {
                throw new Error('cloud updateLogSettings requires a serverId');
            }
            return getRelayBoundary(serverId).putParsed(
                `/relay/proxy/${serverId}/api/v1/logs/settings`,
                doc,
                LogSettingsSchema,
            );
        },
        resetLogSettings: (serverId): Promise<LogSettings> => {
            if (!serverId) {
                throw new Error('cloud resetLogSettings requires a serverId');
            }
            return getRelayBoundary(serverId).deleteParsed(
                `/relay/proxy/${serverId}/api/v1/logs/settings`, LogSettingsSchema,
            );
        },
        listLogFiles: (serverId): Promise<LogFile[]> => {
            if (!serverId) {
                throw new Error('cloud listLogFiles requires a serverId');
            }
            return getRelayBoundary(serverId).fetchRows(
                `/relay/proxy/${serverId}/api/v1/logs/files`,
                LogFileSchema,
                { getKey: getLogFileKey },
            );
        },
        logFileUrl: (serverId, component, name): string => {
            if (!serverId) {
                throw new Error('cloud logFileUrl requires a serverId');
            }
            return `/relay/proxy/${serverId}${logFilePath(component, name)}`;
        },
        fetchLogFile: (serverId, component, name, opts = {}): Promise<LogFileContent> => {
            if (!serverId) {
                throw new Error('cloud fetchLogFile requires a serverId');
            }
            const url = `/relay/proxy/${serverId}${logFilePath(component, name)}`;
            return fetchText(url, {
                range: logFileRange(opts.tailBytes),
                onProgress: opts.onProgress,
                signal: opts.signal,
            });
        },

        // ?orgId= names the organization that claims the server.
        getPendingServer: (code): Promise<PendingServer> => {
            const path = `/vms/api/v1/provisioning/pending/${encodeURIComponent(code)}`;
            return fetchParsed(
                addQuery(path, `orgId=${requireOrg()}`), PendingServerSchema,
            );
        },
        claimServer: (code): Promise<ClaimedServer> => postParsed(
            addQuery('/vms/api/v1/provisioning/claim', `orgId=${requireOrg()}`),
            { code },
            ClaimedServerSchema,
        ),

        // Metadata
        searchEvents: (query = ''): Promise<EventSearchResult[]> =>
            fetchRows(meta('/events/search', query), EventSearchResultSchema, {
                getKey: keyBy('id'),
            }),
        // "More like this": the indexed vector of the object, and its ReID class as
        // `category`. A POST carries the organization in its body.
        searchEventsByVector: (imageEmbedding, opts = {}): Promise<EventSearchResult[]> =>
            postParsed(
                meta('/events/search'),
                {
                    organizationId: requireOrg(),
                    imageEmbedding,
                    ...opts,
                },
                listOf(EventMetadataWithSimilaritySchema, 'events', { getKey: keyBy('id') }),
            ),
        // JOIN-free; it also returns rows with only an embedding, such as scene_embedding.
        searchEmbeddings: (query = ''): Promise<EmbeddingHit[]> =>
            fetchRows(meta('/embeddings/search', query), EmbeddingHitSchema, {
                getKey: keyBy('id'),
            }),
        getEventSegments: (cameraId, opts): Promise<HeatmapSegments> =>
            fetchParsed(
                meta('/events/segments', addQuery(`deviceId=${cameraId}`,
                    getSegmentsQuery(opts))),
                HeatmapSegmentsSchema,
            ),
        getHeatmapSegments: (cameraId, opts): Promise<HeatmapSegments> =>
            fetchParsed(
                meta('/heatmap/segments', addQuery(`deviceId=${cameraId}`,
                    getSegmentsQuery(opts))),
                HeatmapSegmentsSchema,
            ),
        // Raw heatmap rows, each with its 32×56 motion `region`; the segments endpoints merge
        // runs and drop the region.
        getHeatmapRecords: (cameraId, opts): Promise<HeatmapRecord[]> =>
            fetchRows(
                meta('/heatmap', addQuery(`deviceId=${cameraId}`,
                    getHeatmapRecordsQuery(opts))),
                HeatmapRecordSchema,
            ),
        // For <img src>: the cookie authenticates the subresource request.
        getEventImageUrl: (id): string =>
            `/metadata/api/v1/events/${id}/images?orgId=${requireOrg()}`,

        // Enrollments: named ReID identities that vms_database owns and syncs to the edge.
        listEnrollments: (): Promise<Enrollment[]> =>
            fetchRows(v1('/enrollments'), EnrollmentSchema, {
                getKey: keyBy('enrollmentId'),
            }),
        // Matches the stored objects again with the current galleries and thresholds: a
        // verdict is written when an object arrives. It takes seconds for a large org.
        reidentifyEnrollments: (opts = {}): Promise<ReidentifyResponse> =>
            postParsed(
                meta('/enrollments/reidentify'),
                {
                    organizationId: requireOrg(),
                    ...opts,
                },
                ReidentifyResponseSchema,
            ),
        // The vectors are large: only a search from the gallery asks for them.
        getEnrollment: (id, { withEmbeddings = false } = {}): Promise<EnrollmentDetail> =>
            fetchParsed(
                v1(`/enrollments/${id}`, withEmbeddings ? 'withEmbeddings=true' : ''),
                EnrollmentDetailSchema,
            ),
        // A posted name that exists gets the exemplars appended. `negative` adds
        // counter-examples to a name that exists (422 when it does not).
        createEnrollment: ({ name, negative: isNegative, exemplars }): Promise<EnrollResponse> =>
            postParsed(
                v1('/enrollments'),
                {
                    name,
                    negative: isNegative === true,
                    exemplars,
                },
                EnrollResponseSchema,
            ),
        // Matching thresholds, per organization. PUT replaces the whole object, which is judged
        // as one. DELETE returns to the shipped defaults, so a later change of them reaches it.
        getEnrollmentSettings: (): Promise<EnrollmentSettings> =>
            fetchParsed(v1('/enrollments/settings'), EnrollmentSettingsSchema),
        setEnrollmentSettings: (settings): Promise<EnrollmentSettings> =>
            putParsed(v1('/enrollments/settings'), settings, EnrollmentSettingsSchema),
        resetEnrollmentSettings: (): Promise<EnrollmentSettings> =>
            deleteParsed(v1('/enrollments/settings'), EnrollmentSettingsSchema),

        getArchiveEncryption: (): Promise<ArchiveEncryptionStatus> =>
            fetchParsed(v1('/archive-encryption'), ArchiveEncryptionStatusSchema),
        // Nobody reads these answers: the panel reads the status again after its writes.
        setArchiveEncryptionKey: (body): Promise<unknown> =>
            postParsed(v1('/archive-encryption/key'), body, z.unknown()),
        enableArchiveEncryption: (): Promise<unknown> =>
            postParsed(v1('/archive-encryption/enable'), {}, z.unknown()),
        disableArchiveEncryption: (): Promise<unknown> =>
            postParsed(v1('/archive-encryption/disable'), {}, z.unknown()),
        unlockArchiveEncryption: (body): Promise<unknown> =>
            postParsed(v1('/archive-encryption/unlock'), body, z.unknown()),

        // `color: ''` clears the colour. The answer is stale, so it is checked, not returned.
        updateEnrollment: async (id, patch): Promise<void> => {
            await patchParsed(v1(`/enrollments/${id}`), patch, EnrollmentSchema);
        },
        deleteEnrollment: (id): Promise<null> =>
            deleteParsed(v1(`/enrollments/${id}`), z.null()),
        deleteEnrollmentExemplar: (id, exemplarId): Promise<null> =>
            deleteParsed(v1(`/enrollments/${id}/exemplars/${exemplarId}`), z.null()),
        // `bust` refreshes the cached image when the gallery changes.
        getEnrollmentImageUrl: (id, bust = ''): string => {
            const base = `/vms/api/v1/enrollments/${id}/image?orgId=${requireOrg()}`;
            return bust !== '' ? `${base}&v=${bust}` : base;
        },
        getEnrollmentExemplarImageUrl: (id, exemplarId): string => {
            const base = `/vms/api/v1/enrollments/${id}/exemplars/${exemplarId}`;
            return `${base}/image?orgId=${requireOrg()}`;
        },

        // Member permissions are administration, so admin-only, unlike the roster of /users.
        listMemberPermissions: (): Promise<MembershipPermissions[]> =>
            fetchRows(v1('/members/permissions'), MembershipPermissionsSchema),
        getMemberPermissions: (email): Promise<MembershipPermissions> =>
            fetchParsed(
                v1(`/members/${encodeURIComponent(email)}/permissions`),
                MembershipPermissionsSchema,
            ),
        // Role, scope and schedule are one statement, so a replace.
        setMemberPermissions: (email, body): Promise<MembershipPermissions> =>
            putParsed(
                v1(`/members/${encodeURIComponent(email)}/permissions`), body,
                MembershipPermissionsSchema,
            ),

        // Locations: named, flat groups of servers.
        listLocations: (): Promise<LocationWithServers[]> =>
            fetchRows(v1('/locations'), LocationWithServersSchema),
        createLocation: (name): Promise<LocationWithServers> =>
            postParsed(v1('/locations'), { name }, LocationWithServersSchema),
        renameLocation: (id, name): Promise<Location> =>
            patchParsed(v1(`/locations/${id}`), { name }, LocationSchema),
        deleteLocation: (id): Promise<null> => deleteParsed(v1(`/locations/${id}`),
            z.null()),
        // Replace, not merge, so removing a server is expressible.
        setLocationServers: (id, serverIds): Promise<LocationWithServers> =>
            putParsed(v1(`/locations/${id}/servers`), { serverIds },
                LocationWithServersSchema),

        // A create posts a one-element batch; the id is minted here, so no read follows.
        listBookmarks: (filter = {}): Promise<Bookmark[]> =>
            fetchRows(v1('/bookmarks', buildBookmarksQuery(filter)), BookmarkSchema),
        createBookmark: async (draft): Promise<BookmarkDraft & { id: string }> => {
            const bookmark = {
                ...draft,
                id: draft.id ?? uuid(),
            };
            await postParsed(v1('/bookmarks'), [bookmark], BookmarksAcceptedSchema);
            return bookmark;
        },
        updateBookmark: (id, patch): Promise<Bookmark> =>
            patchParsed(v1(`/bookmarks/${id}`), patch, BookmarkSchema),
        deleteBookmark: (id): Promise<null> => deleteParsed(v1(`/bookmarks/${id}`),
            z.null()),
        listBookmarkTags: (limit = 5): Promise<BookmarkTag[]> =>
            fetchRows(v1('/bookmark-tags', `limit=${limit}`), BookmarkTagSchema),

        listUsers: (): Promise<User[]> => fetchRows(v1('/users'), UserSchema),

        // Audit trail
        listAudit: (filter = {}): Promise<AuditRecord[]> =>
            fetchRows(v1('/audit', buildAuditQuery(filter)), AuditRecordSchema),
        addUser: (email): Promise<User> => postParsed(v1('/users/invite'),
            { email }, UserSchema),
        removeUser: (email): Promise<UserDeleteResponse> =>
            deleteParsed(v1(`/users/${encodeURIComponent(email)}`),
                UserDeleteResponseSchema),
        revokeUserSessions: (email): Promise<RevokeSessionsResponse> => postParsed(
            v1(`/users/${encodeURIComponent(email)}/sessions/revoke`), {},
            RevokeSessionsResponseSchema,
        ),

        listHealthLog: (filter = {}): Promise<HealthMessagePayload[]> =>
            fetchRows(v1('/health/log', buildHealthLogQuery(filter)),
                HealthMessagePayloadSchema),
        getHealthCounters: (serverId, group): Promise<HealthCounterValues> => {
            if (!serverId) {
                throw new Error('cloud getHealthCounters requires a serverId');
            }
            return getRelayBoundary(serverId).fetchParsed(
                addQuery(
                    `/relay/proxy/${serverId}/api/v1/health/counters`,
                    queryOf({ group })),
                HealthCounterValuesSchema);
        },
        getHealthCounterDescriptions: (serverId): Promise<HealthCounterDescription[]> => {
            if (!serverId) {
                throw new Error('cloud getHealthCounterDescriptions requires a serverId');
            }
            return getRelayBoundary(serverId).fetchRows(
                `/relay/proxy/${serverId}/api/v1/health/counters/descriptions`,
                HealthCounterDescriptionSchema,
                { getKey: getCounterPathKey });
        },
        getHealthRules: (serverId): Promise<HealthRule[]> => {
            if (!serverId) {
                throw new Error('cloud getHealthRules requires a serverId');
            }
            return getRelayBoundary(serverId).fetchRows(
                `/relay/proxy/${serverId}/api/v1/health/rules`,
                HealthRuleSchema,
                { getKey: getRuleKey });
        },
        // The backend binds the session's identity, so these take no user.
        createTelegramLink: (): Promise<TelegramLink> =>
            postParsed(v1('/telegram/link'), {}, TelegramLinkSchema),
        getTelegramStatus: (): Promise<TelegramStatus> =>
            fetchParsed(v1('/telegram/status'), TelegramStatusSchema),
        unlinkTelegram: (): Promise<null> => deleteParsed(v1('/telegram/link'), z.null()),
    };
}
